import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import moment from 'moment';
import './styles.scss';
import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import HorizontalSlider from '../HorizontalSlider';
import Rating from '../Rating';
import { searchReviews, searchProducts } from './graphql';
import { signup } from '~redux/auth/reducers/signup';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import { Product, Review } from '~types/api';
import { getPublicImageUrl } from '~utils/image';
import { useGraphql } from '~view/hooks/graphql';

type ProductReviewListProps = {
  brandId: string | undefined;
  productId?: any;
  isTab?: boolean;
  containerClass?: string;
};

type TabPanelProps = {
  children?: React.ReactNode;
  index: any;
  value: any;
};

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
};

const a11yProps = (index: any) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const ProductReviewList: React.FC<ProductReviewListProps> = ({
  brandId,
  productId,
  isTab,
  containerClass,
}) => {
  const auth = useSelector(authUserSelector);
  const [review, fetchReview] = useGraphql<{
    items: Review[];
    nextToken?: string;
  }>(searchReviews, (data) => data.searchReviews, !auth);
  const [products, fetchProducts] = useGraphql<Product[]>(
    searchProducts,
    (data) => data.searchProducts.items,
    !auth
  );
  const [value, setValue] = useState(0);
  const dispatch = useDispatch();
  const handleProductClick = useCallback(
    (e) => {
      if (auth) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      dispatch(signup({ nextPath: e.currentTarget.pathname }));
    },
    [auth]
  );

  useEffect(() => {
    if (!brandId) {
      return;
    }
    fetchReview({
      filter: { brand_id: { eq: brandId } },
    });
  }, [brandId]);

  useEffect(() => {
    if (!review?.items?.length) {
      return;
    }
    fetchProducts({
      filter: {
        or: review.items.flatMap((review) =>
          review.product_ids?.map((productId) => ({
            id: { eq: productId },
          }))
        ),
      },
    });
  }, [review]);

  const reviewList = useMemo(
    () =>
      review?.items?.map((review: any) => ({
        id: review.id,
        ids: review.product_ids,
        brandId: review.brand_id,
        rate: review.rate,
        title: review.title,
        comment: review.comment,
        createdAt: review.createdAt,
        replies: review.replies.items,
      })) ?? [],
    [review]
  );

  useEffect(() => {
    if (reviewList.some((review) => review.ids.includes(productId))) {
      setValue(1);
      return;
    }
  }, [reviewList, productId]);

  const totalRateAverage = useMemo(
    () =>
      review != undefined
        ? reviewList.reduce(
            (sum, i) =>
              Math.round((sum + i.rate / review?.items?.length) * 10) / 10,
            0
          )
        : 0,
    [review, reviewList]
  );

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  const allReviews = reviewList.map((review) => {
    return (
      <div className="review" key={review.id}>
        <div className="rate-wrapper">
          <Rating name="rate-rating" value={review.rate} readOnly />
          <p className="date">
            {moment(review.createdAt).format('YYYY/MM/DD')}
          </p>
        </div>
        <p className="font-weight-bold">{review.title}</p>
        <p>{review.comment}</p>
        {review.replies.map((reply: any) => {
          return (
            <p className="bg-light p-4">
              <p className="font-weight-bold" translate="no">
                {products?.[0]?.brand?.brand_name}
              </p>
              {reply.comment}
            </p>
          );
        })}
        <div style={{ maxWidth: '400px' }}>
          <HorizontalSlider
            xs={5}
            sm={5}
            md={5}
            lg={5}
            xl={5}
            contentAlign="left"
          >
            {products
              ?.filter((product) => review.ids.includes(product.id))
              ?.map((product) => (
                <div key={product.id}>
                  <Link
                    to={`/productdetail/${product.id}`}
                    onClick={handleProductClick}
                  >
                    <img
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'contain',
                      }}
                      src={getPublicImageUrl(
                        product?.imageKeys?.items?.[0]?.imageKey ?? undefined,
                        { w: 60, h: 60 }
                      )}
                    />
                  </Link>
                </div>
              ))}
          </HorizontalSlider>
        </div>
      </div>
    );
  });

  const filterReviews = reviewList.filter((item) =>
    item.ids.includes(productId)
  );

  const purchasedReviews = filterReviews?.map((review) => (
    <div className="review" key={review.id}>
      <div className="rate-wrapper">
        <Rating name="rate-rating" value={review.rate} readOnly />
        <p className="date">{moment(review.createdAt).format('YYYY/MM/DD')}</p>
      </div>
      <p className="font-weight-bold">{review.title}</p>
      <p>{review.comment}</p>
      {review.replies.map((reply: any) => {
        return (
          <p className="bg-light p-4">
            <p className="font-weight-bold" translate="no">
              {products?.[0]?.brand?.brand_name}
            </p>
            {reply.comment}
          </p>
        );
      })}
      <div style={{ maxWidth: '400px' }}>
        <HorizontalSlider
          xs={5}
          sm={5}
          md={5}
          lg={5}
          xl={5}
          contentAlign="left"
        >
          {products
            ?.filter((product) => review.ids.includes(product.id))
            ?.map((product) => (
              <div key={product.id}>
                <Link
                  to={`/productdetail/${product.id}`}
                  onClick={handleProductClick}
                >
                  <img
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'contain',
                    }}
                    src={getPublicImageUrl(
                      product?.imageKeys?.items?.[0]?.imageKey ?? undefined,
                      { w: 60, h: 60 }
                    )}
                  />
                </Link>
              </div>
            ))}
        </HorizontalSlider>
      </div>
    </div>
  ));

  if (reviewList.length == 0) return null;

  if (isTab == false) {
    return (
      <section
        className={`review-list ${containerClass}`}
        id={`review-list-${brandId}`}
      >
        <h4>{reviewList.length} 件のブランドレビュー</h4>
        <div className="rate-wrapper mb-5">
          <Rating
            name="rate-rating"
            size="large"
            precision={0.5}
            value={totalRateAverage}
            readOnly
          />
          <p>{totalRateAverage}</p>
        </div>
        {allReviews}
      </section>
    );
  }

  return (
    <section
      className={`review-list ${containerClass}`}
      id={`review-list-${brandId}`}
    >
      <h4>{reviewList.length} 件のブランドレビュー</h4>
      <div className="rate-wrapper">
        <Rating
          name="rate-rating"
          size="large"
          precision={0.5}
          value={totalRateAverage}
          readOnly
        />
        <p>{totalRateAverage}</p>
      </div>
      <Tabs
        value={value}
        onChange={handleChange}
        TabIndicatorProps={{
          style: {
            backgroundColor: '#101010',
          },
        }}
        className="tabs"
      >
        <Tab
          label={'全てのレビュー' + '(' + allReviews.length + ')'}
          disabled={allReviews.length == 0 ? true : false}
          {...a11yProps(0)}
        />
        <Tab
          label={'この商品のレビュー' + '(' + purchasedReviews.length + ')'}
          disabled={purchasedReviews.length == 0 ? true : false}
          {...a11yProps(1)}
        />
      </Tabs>
      <TabPanel value={value} index={0}>
        {allReviews}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {purchasedReviews}
      </TabPanel>
    </section>
  );
};

export default ProductReviewList;
